import { AuditOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Card from 'components/@extended/Card'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import ComponentDisplay from 'components/ComponentDisplay'
import ComponentField from 'components/ComponentField'
import { postWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useFetchTargetDetails, useDeleteTarget } from 'hooks/useComponents'
import { useSnackbar } from 'notistack'
import type { FC, JSX } from 'react'
import { startTransition, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ComponentSourceType, ComponentType, CompositeType } from 'types/components'
import { formatDisplayDateTime } from 'types/periods'

interface ColumnProps {
  id: string
  label: string
  minWidth: number
  align?: 'right' | 'left' | 'inherit' | 'center' | 'justify' | undefined
  format?: (value: any, row: any) => string | JSX.Element
}

const EditTarget: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const componentId = searchParams.get('componentId')
  const organizationId = searchParams.get('organizationId')
  const periodId = searchParams.get('periodId')
  const userId = searchParams.get('userId')
  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [isSaving, setIsSaving] = useState(false)
  const { deleteTarget, isLoading: isDeleting } = useDeleteTarget(token!)

  const shouldFetch = !!token && !!componentId && !!organizationId && !!periodId
  console.log('Should fetch:', shouldFetch, { token, componentId, organizationId, periodId })

  const { targetDetails, isLoading, isError, revalidate } = useFetchTargetDetails(
    token!,
    {
      componentId: componentId!,
      organizationId: organizationId!,
      periodId: periodId!,
      ...(userId !== 'null' && { userId }),
    },
    shouldFetch
  )

  const [targetValue, setTargetValue] = useState('')

  const [compositeValues, setCompositeValues] = useState<{ values: any[] }>({ values: [] })
  const [compositeType, setCompositeType] = useState<CompositeType>(CompositeType.COMPOSITE_TYPE_SIMPLE)

  useEffect(() => {
    if (targetDetails) {
      console.log('Processing targetDetails:', {
        sourceType: targetDetails.component.sourceType,
        revisionValue: targetDetails.revisions[0].value,
        isComposite: targetDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE,
      })

      // Parse compositeType from component sourceInfo
      try {
        if (targetDetails.component.sourceInfo) {
          const sourceInfo =
            typeof targetDetails.component.sourceInfo === 'string'
              ? JSON.parse(targetDetails.component.sourceInfo)
              : targetDetails.component.sourceInfo
          setCompositeType(sourceInfo.compositeType || CompositeType.COMPOSITE_TYPE_SIMPLE)
        } else {
          setCompositeType(CompositeType.COMPOSITE_TYPE_SIMPLE)
        }
      } catch {
        setCompositeType(CompositeType.COMPOSITE_TYPE_SIMPLE)
      }

      if (targetDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        try {
          const parsed = JSON.parse(targetDetails.revisions[0].value)
          console.log('JSON parsed successfully:', parsed)

          // Handle both structures: {values: [...]} and {compositeTargetValues: [...]}
          if (parsed.compositeTargetValues && Array.isArray(parsed.compositeTargetValues)) {
            console.log('Using compositeTargetValues structure')
            setCompositeValues({ values: parsed.compositeTargetValues })
            // Extract the overall value from the composite structure
            setTargetValue(parsed.value || '0')
          } else if (parsed.values && Array.isArray(parsed.values)) {
            console.log('Using legacy values structure')
            setCompositeValues({ values: parsed.values })
            // For legacy format, use the entire JSON as the target value
            setTargetValue(targetDetails.revisions[0].value)
          } else {
            console.log('JSON parsed but no expected structure, using parsed value:', parsed)
            // If JSON parses but doesn't have expected structure, use the parsed value or fallback to raw value
            setCompositeValues({ values: [] })
            setTargetValue(
              typeof parsed === 'string' || typeof parsed === 'number'
                ? String(parsed)
                : targetDetails.revisions[0].value
            )
          }
        } catch (error) {
          console.log('JSON parse failed, using raw value:', targetDetails.revisions[0].value)
          // Backward compatibility: If JSON parsing fails for composite targets,
          // treat the value as a simple value and initialize empty composite values
          setCompositeValues({ values: [] })
          setTargetValue(targetDetails.revisions[0].value)
        }
      } else {
        console.log('Non-composite target, using value directly:', targetDetails.revisions[0].value)
        // For non-composite targets, use the value directly
        setTargetValue(targetDetails.revisions[0].value)
      }
    }
  }, [targetDetails])

  // Composite handlers
  const handleAddCompositeValue = () => {
    const newCompositeValues = {
      values: [
        ...compositeValues.values,
        {
          label: `Target ${compositeValues.values.length + 1}`,
          description: 'Enter description',
          target: '0',
          componentType: ComponentType.COMPONENT_TYPE_PERCENTAGE,
        },
      ],
    }
    setCompositeValues(newCompositeValues)
    // Don't update targetValue here - keep the overall value intact
  }

  const handleRemoveCompositeValue = (valueIndex: number) => {
    const newValues = {
      values: compositeValues.values.filter((_, i) => i !== valueIndex),
    }
    setCompositeValues(newValues)
    // Don't update targetValue here - keep the overall value intact
  }

  const handleUpdateCompositeValue = (
    valueIndex: number,
    field: 'label' | 'description' | 'target',
    newValue: string
  ) => {
    const newValues = {
      values: compositeValues.values.map((item, i) => (i === valueIndex ? { ...item, [field]: newValue } : item)),
    }
    setCompositeValues(newValues)
    // Don't update targetValue here - keep the overall value intact
  }

  const handleUpdateComponentType = (valueIndex: number, newType: ComponentType) => {
    const newValues = {
      values: compositeValues.values.map((item, i) => (i === valueIndex ? { ...item, componentType: newType } : item)),
    }
    setCompositeValues(newValues)
    // Don't update targetValue here - keep the overall value intact
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await postWithToken('components/targets/update', token!, {
        componentId,
        periodId,
        organizationId,
        value:
          targetDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
            ? JSON.stringify({
                value: targetValue, // Use the actual target value from state
                compositeTargetValues: compositeValues.values,
              })
            : targetValue,

        ...(userId !== 'null' && { userId }),
      })

      // Handle successful response
      enqueueSnackbar('Target updated successfully', { variant: 'success' })

      // Revalidate the target details
      revalidate()
    } catch (error: any) {
      // Handle different error scenarios
      if (error.response) {
        switch (error.response.status) {
          case 400:
            enqueueSnackbar('Invalid input data. Please check your entries.', { variant: 'error' })
            break
          case 403:
            enqueueSnackbar('You do not have permission to update this target.', { variant: 'error' })
            break
          default:
            enqueueSnackbar('Failed to update target. Please try again.', { variant: 'error' })
        }
      } else {
        enqueueSnackbar('An unexpected error occurred. Please try again.', { variant: 'error' })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelConfirmation = () => {
    setOpenCancelDialog(false)
    startTransition(() => {
      navigate('/admin/components/targets')
    })
  }

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleConfirmDelete = async () => {
    try {
      // Get the latest revision number
      const latestRevision = targetDetails?.revisions
        ? Math.max(...targetDetails.revisions.map((r) => r.revision))
        : undefined

      await deleteTarget({
        componentId: componentId!,
        organizationId: organizationId!,
        periodId: periodId!,
        ...(userId !== 'null' && { userId }),
        ...(latestRevision !== undefined && { revision: latestRevision }),
      })

      enqueueSnackbar('Target deleted successfully', { variant: 'success' })

      // Check if this was the last revision - if so, navigate back to targets page
      // Otherwise, revalidate to refresh the data
      if (targetDetails?.revisions && targetDetails.revisions.length <= 1) {
        // This was the last (or only) revision, navigate back to targets page
        startTransition(() => {
          navigate('/admin/components/targets')
        })
      } else {
        // There are more revisions, just refresh the data
        revalidate()
        setOpenDeleteDialog(false)
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete target'
      enqueueSnackbar(errorMessage, { variant: 'error' })
      setOpenDeleteDialog(false)
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (isError || !targetDetails) {
    return <Typography variant="h6">Error loading target details</Typography>
  }

  const isComposite = targetDetails.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
  const isPercentage = targetDetails.component.componentType === ComponentType.COMPONENT_TYPE_PERCENTAGE

  const orgSettings = targetDetails.coreSettings

  const planColumns: ColumnProps[] = [
    { id: 'name', label: 'Name', minWidth: 170, format: (value, row) => `${row.firstName} ${row.lastName}` },
    {
      id: 'approvalStatus',
      label: 'Status',
      minWidth: 100,
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (value, row) => (
        <Box display="flex" justifyContent="center">
          <Tooltip title="View Plan">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/employee/${row.userId}/compplan`)
                })
              }}
            >
              <EyeOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Plan">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/create-comp-plan?userId=${row.userId}`, {
                    state: { from: location.pathname },
                  })
                })
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Approval Batch">
            <IconButton
              color="primary"
              onClick={() => {
                startTransition(() => {
                  navigate(`/admin/approvals/${row.approvalBatchId}/edit`)
                })
              }}
            >
              <AuditOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  const revisionColumns: ColumnProps[] = [
    { id: 'revision', label: 'Revision', minWidth: 100 },
    {
      id: 'approvalStatus',
      label: 'Status',
      minWidth: 150,
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    },
    {
      id: 'value',
      label: 'Value',
      minWidth: 120,
      format: (value) => (
        <ComponentDisplay
          componentType={targetDetails.component.componentType as ComponentType}
          value={value}
          currency={orgSettings.currency}
          currencyDecimals={orgSettings.currencyDecimals}
          fontSize="inherit"
          sourceType={targetDetails.component.sourceType as ComponentSourceType}
          isTarget={true}
        />
      ),
    },
    { id: 'createdAt', label: 'Created At', minWidth: 170, format: (value) => formatDisplayDateTime(value) },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'center',
      format: (value, row) => {
        const hasTemplates = targetDetails?.templates && targetDetails.templates.length > 0
        const isLatestRevision =
          targetDetails?.revisions && row.revision === Math.max(...targetDetails.revisions.map((r) => r.revision))

        return (
          <Box display="flex" justifyContent="center">
            {row.approvalBatchId && (
              <Tooltip title="View Approval Batch">
                <IconButton
                  color="primary"
                  onClick={() => {
                    startTransition(() => {
                      navigate(`/admin/approvals/${row.approvalBatchId}/edit`)
                    })
                  }}
                >
                  <AuditOutlined />
                </IconButton>
              </Tooltip>
            )}
            {isLatestRevision && (
              <Tooltip title={hasTemplates ? 'Cannot delete - this target is used by templates' : 'Delete Target'}>
                <span>
                  <IconButton color="error" onClick={handleDeleteClick} disabled={hasTemplates || isDeleting}>
                    <DeleteOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <Typography variant="h2" gutterBottom>
        Edit Target
      </Typography>
      {isComposite && isPercentage ? (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Component Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2, pl: 2, pr: 2 }}>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Component:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.component.label}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Team:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.organization.name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Period:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.period.label}
                    </Typography>
                  </Grid>
                  {!targetDetails.component.isOrganizationLevel && (
                    <>
                      <Grid size={{ xs: 4 }}>
                        <Typography>{targetDetails.user ? 'Employee:' : ''}</Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        {userId === '00000000-0000-0000-0000-000000000000' ? (
                          <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                            Default values for employee plans
                          </Typography>
                        ) : targetDetails.user ? (
                          <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                            {`${targetDetails.user.firstName} ${targetDetails.user.lastName}`}
                          </Typography>
                        ) : null}
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Target Value
                </Typography>
                <Box mb={2}>
                  <ComponentField
                    componentType={targetDetails.component.componentType as ComponentType}
                    label="Overall Target Value"
                    value={targetValue}
                    onChange={(value) => {
                      setTargetValue(value)
                    }}
                    name="target-value"
                    currency={orgSettings.currency}
                    currencyDecimals={orgSettings.currencyDecimals}
                    decimalPlaces={2}
                    variant="outlined"
                    shrink
                    pill
                  />
                </Box>
                {(targetDetails.component.targetMin || targetDetails.component.targetMax) && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Component suggested ranges:
                    </Typography>
                    {targetDetails.component.targetMin && (
                      <Typography variant="body2" color="textSecondary">
                        • Minimum:{' '}
                        <ComponentDisplay
                          componentType={targetDetails.component.componentType as ComponentType}
                          value={targetDetails.component.targetMin}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          fontSize="inherit"
                        />
                      </Typography>
                    )}
                    {targetDetails.component.targetMax && (
                      <Typography variant="body2" color="textSecondary">
                        • Maximum:{' '}
                        <ComponentDisplay
                          componentType={targetDetails.component.componentType as ComponentType}
                          value={targetDetails.component.targetMax}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          fontSize="inherit"
                        />
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Card variant="outlined" sx={{ position: 'relative' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Composite Target Values
                </Typography>
                {compositeType === CompositeType.COMPOSITE_TYPE_SIMPLE && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon fontSize="small" color="secondary" />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Note:</strong> For simple composite types, targets are used for display purposes only and
                      are not used in calculations.
                    </Typography>
                  </Box>
                )}
                <Box mb={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button startIcon={<PlusOutlined />} size="small" onClick={handleAddCompositeValue}>
                      Add Value
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                      <TableHead
                        sx={{
                          '& th': {
                            borderTop: `1px solid ${theme.palette.divider}`,
                            borderBottom: `2px solid ${theme.palette.divider} !important`,
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell width="15%">
                            <strong>Label</strong>
                          </TableCell>
                          <TableCell width="40%">
                            <strong>Description</strong>
                          </TableCell>
                          <TableCell width="10%">
                            <strong>Type</strong>
                          </TableCell>
                          <TableCell width="10%">
                            <strong>Target</strong>
                          </TableCell>
                          <TableCell width="5%" align="center">
                            <strong>Actions</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(compositeValues.values ?? []).map((item, valueIndex) => (
                          <TableRow key={valueIndex}>
                            <TableCell>
                              <TextField
                                value={item.label}
                                onChange={(e) => handleUpdateCompositeValue(valueIndex, 'label', e.target.value)}
                                fullWidth
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={item.description}
                                onChange={(e) => handleUpdateCompositeValue(valueIndex, 'description', e.target.value)}
                                fullWidth
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.componentType || targetDetails.component.componentType}
                                onChange={(e) => handleUpdateComponentType(valueIndex, e.target.value as ComponentType)}
                                fullWidth
                                variant="outlined"
                                disabled={!isPercentage}
                                sx={{ fontSize: 'inherit', borderRadius: '21px' }}
                              >
                                <MenuItem value={ComponentType.COMPONENT_TYPE_INTEGER}>Integer</MenuItem>
                                <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>Currency</MenuItem>
                                <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>Percentage</MenuItem>
                                <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                                <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Decimal</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <ComponentField
                                componentType={
                                  item.componentType || (targetDetails.component.componentType as ComponentType)
                                }
                                label=""
                                value={item.target || ''}
                                onChange={(value) => handleUpdateCompositeValue(valueIndex, 'target', value)}
                                name={`composite-target-${valueIndex}`}
                                currency={orgSettings.currency}
                                currencyDecimals={orgSettings.currencyDecimals}
                                decimalPlaces={2}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => handleRemoveCompositeValue(valueIndex)}>
                                <DeleteOutlined />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(compositeValues.values ?? []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="textSecondary">
                                No values added yet. Click "Add Value" to begin.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 4 }}>
                  <Stack direction="row" spacing={2}>
                    <AnimateButton>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                          setOpenCancelDialog(true)
                        }}
                      >
                        Cancel
                      </Button>
                    </AnimateButton>
                    <AnimateButton>
                      <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                      </Button>
                    </AnimateButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        // Regular Target Layout (2-column)
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Component Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2, pl: 2, pr: 2 }}>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Component:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.component.label}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Team:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.organization.name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography>Period:</Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                      {targetDetails.period.label}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    {!targetDetails.component.isOrganizationLevel && (
                      <>
                        <Grid size={{ xs: 4 }}>
                          <Typography>{targetDetails.user ? 'Employee:' : ''}</Typography>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          {userId === '00000000-0000-0000-0000-000000000000' ? (
                            <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                              Values represent defaults used when creating employee plans
                            </Typography>
                          ) : targetDetails.user ? (
                            <Typography fontWeight="bold" color="primary" sx={{ textAlign: 'right' }}>
                              {`${targetDetails.user.firstName} ${targetDetails.user.lastName}`}
                            </Typography>
                          ) : null}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%', position: 'relative' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Target Values
                </Typography>
                <Box mb={2}>
                  <ComponentField
                    componentType={targetDetails.component.componentType as ComponentType}
                    label="Value *"
                    value={targetValue}
                    onChange={(value) => {
                      setTargetValue(value)
                    }}
                    name="target-value"
                    currency={orgSettings.currency}
                    currencyDecimals={orgSettings.currencyDecimals}
                    decimalPlaces={2}
                    variant="outlined"
                    shrink
                    pill
                  />
                </Box>
                {(targetDetails.component.targetMin || targetDetails.component.targetMax) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      Component suggested ranges:
                    </Typography>
                    {targetDetails.component.targetMin && (
                      <Typography variant="body2" color="textSecondary">
                        • Minimum:{' '}
                        <ComponentDisplay
                          componentType={targetDetails.component.componentType as ComponentType}
                          value={targetDetails.component.targetMin}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          fontSize="inherit"
                        />
                      </Typography>
                    )}
                    {targetDetails.component.targetMax && (
                      <Typography variant="body2" color="textSecondary">
                        • Maximum:{' '}
                        <ComponentDisplay
                          componentType={targetDetails.component.componentType as ComponentType}
                          value={targetDetails.component.targetMax}
                          currency={orgSettings.currency}
                          currencyDecimals={orgSettings.currencyDecimals}
                          fontSize="inherit"
                        />
                      </Typography>
                    )}
                  </Box>
                )}
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <Stack direction="row" spacing={2}>
                    <AnimateButton>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                          setOpenCancelDialog(true)
                        }}
                      >
                        Cancel
                      </Button>
                    </AnimateButton>
                    <AnimateButton>
                      <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                      </Button>
                    </AnimateButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Target Revisions
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                {revisionColumns.map((column) => (
                  <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                    <strong>{column.label}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {targetDetails.revisions
                .sort((a, b) => b.revision - a.revision)
                .map((revision) => (
                  <TableRow key={revision.revision}>
                    {revisionColumns.map((column) => {
                      const value = revision[column.id as keyof typeof revision]
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format ? column.format(value, revision) : value}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Employee Plans using this Target
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                {planColumns.map((column) => (
                  <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                    <strong>{column.label}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {targetDetails.plans.map((plan) => (
                <TableRow key={plan.id}>
                  {planColumns.map((column) => {
                    const value = plan[column.id as keyof typeof plan]
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format ? column.format(value, plan) : value}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>
        Templates using this Component
      </Typography>
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead
              sx={{
                '& th': {
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderBottom: `2px solid ${theme.palette.divider} !important`,
                },
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Label</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {targetDetails.templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.label}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit Template">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          startTransition(() => {
                            navigate(`/admin/templates/${template.id}/edit`)
                          })
                        }}
                      >
                        <EditOutlined />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle>Confirm cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to cancel updating this target?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No</Button>
          <Button onClick={handleCancelConfirmation} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Target Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the latest revision of this target?</DialogContentText>
          {targetDetails?.revisions && targetDetails.revisions.length > 1 ? (
            <DialogContentText sx={{ mt: 2, color: 'info.main' }}>
              Note: This will only delete revision {Math.max(...targetDetails.revisions.map((r) => r.revision))}. The
              other {targetDetails.revisions.length - 1} revision{targetDetails.revisions.length > 2 ? 's' : ''} will
              remain.
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
              Warning: This is the only revision. Deleting it will remove the entire target.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete Target'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditTarget
